<template>
  <component
    :is="isClickableComputed ? 'a' : 'article'"
    class="background-card"
    :class="cardClasses"
    :style="backgroundStyle"
    :href="isClickableComputed ? link : null"
  >
    <div
      class="background-card__content"
      :class="`background-card__content--align-${contentAlign}`"
    >
      <p
        v-if="hasOverlineSlot"
        class="background-card__overline"
        :class="`background-card__overline--${overlineSize}`"
        v-html="truncatedOverline"
      />

      <component
        :is="headingTag"
        class="background-card__title"
        :class="`background-card__title--${titleSize}`"
        v-html="truncatedTitle"
      />

      <div
        v-if="hasDescriptionSlot"
        class="background-card__description"
        :class="`background-card__description--${descriptionSize}`"
        v-html="truncatedDescription"
      />

      <div v-if="!isClickableComputed && hasButtonSlot" class="background-card__button">
        <slot name="button" />
      </div>
    </div>
  </component>
</template>

<script>
export default {
  name: 'background-card',
  props: {
    ariaLabel: {
      type: String,
    },
    backgroundImage: {
      type: String,
      default: null,
    },
    backgroundColor: {
      type: String,
      default: '#000',
    },
    borderColor: {
      type: String,
      default: 'none',
    },
    titleColor: {
      type: String,
      default: '#fff',
    },
    descriptionColor: {
      type: String,
      default: '#fff',
    },
    overlineColor: {
      type: String,
      default: '#fff',
    },
    headingTag: {
      type: String,
      default: 'h4',
      validator: (value) => ['h2', 'h3', 'h4', 'h5', 'h6'].includes(value),
    },
    titleSize: {
      type: String,
      default: 'lg',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
    },
    overlineSize: {
      type: String,
      default: 'xs',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
    },
    descriptionSize: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
    },
    isClickable: {
      type: Boolean,
      default: false,
    },
    link: {
      type: String,
      default: null,
    },
    contentAlign: {
      type: String,
      default: 'center',
      validator: (value) => ['top', 'center', 'bottom'].includes(value),
    },
    overlineMaxChars: {
      type: Number,
      default: 25,
    },
    titleMaxChars: {
      type: Number,
      default: 60,
    },
    descriptionMaxChars: {
      type: Number,
      default: 140,
    },
  },
  computed: {
    isClickableComputed() {
      return this.isClickable || !!this.link
    },
    truncatedOverline() {
      const text = this.getSlotText(this.$slots.overline)
      return this.truncateText(text, this.overlineMaxChars)
    },
    truncatedTitle() {
      const text = this.getSlotText(this.$slots.title)
      return this.truncateText(text, this.titleMaxChars)
    },
    truncatedDescription() {
      const text = this.getSlotText(this.$slots.description)
      return this.truncateText(text, this.descriptionMaxChars)
    },
    hasImage() {
      return Boolean(this.backgroundImage)
    },
    hasOverlineSlot() {
      const slot = this.$slots.overline
      return Array.isArray(slot) ? slot.length > 0 : !!slot
    },
    hasDescriptionSlot() {
      const slot = this.$slots.description
      return Array.isArray(slot) ? slot.length > 0 : !!slot
    },
    hasButtonSlot() {
      const slot = this.$slots.button
      return Array.isArray(slot) ? slot.length > 0 : !!slot
    },
    backgroundStyle() {
      return {
        '--bg-image': this.hasImage ? `url(${this.backgroundImage})` : '',
        '--title-color': this.titleColor,
        '--bg-color': this.backgroundColor,
        '--border-color': this.borderColor,
        '--description-color': this.descriptionColor,
        '--overline-color': this.overlineColor,
      }
    },
    cardClasses() {
      return {
        'background-card--clickable': this.isClickableComputed,
        'background-card--no-image': !this.hasImage,
      }
    },
  },
  methods: {
    getSlotText(slot) {
      if (!slot) return ''

      const getText = (vnode) => {
        if (!vnode) return ''
        if (typeof vnode === 'string') return vnode
        if (Array.isArray(vnode)) {
          return vnode.map(getText).join('')
        }
        if (vnode.text) return vnode.text
        if (vnode.children) {
          if (Array.isArray(vnode.children)) {
            return vnode.children.map(getText).join('')
          }
          return getText(vnode.children)
        }
        return ''
      }

      return getText(slot).trim()
    },
    truncateText(text, maxLength) {
      if (!text || text.length <= maxLength) return text
      return `${text.substring(0, maxLength)}...`
    },
  },
}
</script>

<style lang="scss" scoped>
.background-card {
  position: relative;
  display: block;
  width: 100%;
  padding: 1.5rem 1.875rem 1.875rem 1.875rem;
  text-decoration: none;
  overflow: hidden;
  background-color: var(--bg-color);
  border: 0.0625rem solid var(--border-color);
  border-radius: 0.75rem;
  height: 31.25rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: var(--bg-image);
    background-size: cover;
    background-position: center;
    transition: transform 0.8s ease-in-out;
    will-change: transform;
    opacity: 0.7;
  }

  &__content {
    position: relative;
    z-index: 1;
    height: 100%;
    display: flex;
    flex-direction: column;

    &--align-top {
      justify-content: flex-start;
      padding-top: 4rem;
    }

    &--align-center {
      justify-content: center;
      padding: 4rem 0;
    }

    &--align-bottom {
      justify-content: flex-end;
      padding-bottom: 4rem;
    }
  }

  &__overline {
    line-height: 130%;
    color: var(--overline-color);

    &--xl {
      font-size: 1.125rem;

      @media screen and (min-width: 1200px) {
        font-size: 1.5rem;
      }
    }

    &--lg {
      font-size: 1rem;

      @media screen and (min-width: 1200px) {
        font-size: 1.375rem;
      }
    }

    &--md {
      font-size: 0.875rem;

      @media screen and (min-width: 1200px) {
        font-size: 1.25rem;
      }
    }

    &--sm {
      font-size: 0.8125rem;

      @media screen and (min-width: 1200px) {
        font-size: 1.125rem;
      }
    }

    &--xs {
      font-size: 0.75rem;

      @media screen and (min-width: 1200px) {
        font-size: 1rem;
      }
    }
  }

  &__title {
    font-weight: 700;
    line-height: 1.2;
    color: var(--title-color);
    margin-top: 0.5rem;

    &--xl {
      font-size: 2rem;
    }

    &--lg {
      font-size: 1.8rem;
    }

    &--md {
      font-size: 1.6rem;
    }

    &--sm {
      font-size: 1.4rem;
    }

    &--xs {
      font-size: 1.2rem;
    }
  }

  &__description {
    margin-top: 0.5rem;
    color: var(--description-color);

    &--xl {
      font-size: 1.25rem;
    }

    &--lg {
      font-size: 1.125rem;
    }

    &--md {
      font-size: 1rem;
    }

    &--sm {
      font-size: 0.9375rem;
    }

    &--xs {
      font-size: 0.8125rem;
    }
  }

  &__button {
    margin-top: 1rem;
    align-self: flex-start;
  }

  &--clickable {
    cursor: pointer;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 0.0625rem #88D49E ;
  }

  &:hover:not(.background-card--no-image)::before {
    transform: scale(1.1);
  }
}
</style>
